/**
 * Present mode for the planning page: one section at a time, sized for a
 * shared screen, with a clock per agenda item and the decisions beside it.
 * Everything stays live and editable, the same as on the page.
 *
 * Keys: ← → (or Page Up / Page Down, as clickers send) step through the
 * agenda; keys typed into a field stay in the field. No letter or digit
 * shortcuts: speech input would set them off (WCAG 2.1.4), and the buttons
 * cover them.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Caption1,
  FluentProvider,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Spinner,
  ToggleButton,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
  type Theme,
} from '@fluentui/react-components'
import {
  ArrowExit20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  FullScreenMaximize20Regular,
  FullScreenMinimize20Regular,
  PanelRight20Regular,
  Pause16Regular,
  Play16Regular,
} from '@fluentui/react-icons'
import { ErrorCard } from '../components/ErrorCard'
import { DecisionRail } from '../components/planning/DecisionRail'
import { PlanningStatus } from '../components/planning/PlanningStatus'
import { InfoHint, PlanSection, useAgendaStyles } from '../components/planning/PlanParts'
import { spotlight, usePlanSections } from '../components/planning/planSections'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { usePlanningCycle, usePlanningCycles } from '../hooks/usePlanning'
import { apiErrorMessage } from '../lib/apiError'
import { decisionLog, type DecisionItem } from '../lib/planning'
import { contentBg } from '../theme'
import type { PlanningCycle } from '../types/planning'

// About 15% larger type than the app's, so a shared window shrunk into a
// Teams call still reads. Nested, so only this page changes. The provider
// takes no className: Fluent copies it onto every popup's portal, and a
// full-height layout class there blanked the whole screen under a tooltip.
const PRESENT_TYPE: Partial<Theme> = {
  fontSizeBase100: '12px',
  fontSizeBase200: '14px',
  fontSizeBase300: '16px',
  fontSizeBase400: '18px',
  fontSizeBase500: '22px',
  fontSizeBase600: '26px',
  fontSizeHero700: '32px',
  lineHeightBase100: '16px',
  lineHeightBase200: '20px',
  lineHeightBase300: '22px',
  lineHeightBase400: '24px',
  lineHeightBase500: '30px',
  lineHeightBase600: '34px',
  lineHeightHero700: '40px',
}

const useStyles = makeStyles({
  root: {
    height: '100vh',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    background: contentBg,
  },
  center: { display: 'grid', placeItems: 'center', height: '100vh', background: contentBg },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '80px',
    flexShrink: 1,
  },
  // One row, however narrow the shared window: it scrolls sideways instead of wrapping.
  // The agenda gives way first, scrolling sideways, so the tools stay on screen.
  nav: { minWidth: 0, flexShrink: 1, overflowX: 'auto' },
  agenda: { flexWrap: 'nowrap' },
  tools: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  main: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', minHeight: 0 },
  stage: {
    overflowY: 'auto',
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL}`,
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '1600px',
    margin: '0 auto',
  },
  rail: {
    width: '340px',
    overflowY: 'auto',
    padding: tokens.spacingHorizontalL,
    background: tokens.colorNeutralBackground1,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  foot: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    background: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  next: { justifySelf: 'end' },
  clock: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  time: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' },
  digits: { fontVariantNumeric: 'tabular-nums', fontWeight: tokens.fontWeightSemibold },
  over: { color: tokens.colorPaletteDarkOrangeForeground1 },
  muted: { color: tokens.colorNeutralForeground3, fontVariantNumeric: 'tabular-nums' },
  count: { marginLeft: tokens.spacingHorizontalS },
})

const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`

/**
 * Time spent on each section while the clock runs; it starts paused. Measured
 * between timestamps, so a slow or throttled tick (a tab in the background)
 * loses nothing; the tick only repaints.
 */
function useAgendaClock(here: string) {
  const [running, setRunning] = useState(false)
  const [banked, setBanked] = useState<Record<string, number>>({})
  const since = useRef<number | null>(null)
  const [, repaint] = useState(0)
  useEffect(() => {
    if (!running) return
    const start = Date.now()
    since.current = start
    const t = setInterval(() => repaint((n) => n + 1), 1000)
    return () => {
      clearInterval(t)
      since.current = null
      // Banked on the section it was spent on: `here` as it was.
      setBanked((b) => ({ ...b, [here]: (b[here] ?? 0) + Date.now() - start }))
    }
  }, [running, here])
  const ms = (id: string) =>
    (banked[id] ?? 0) + (id === here && since.current != null ? Date.now() - since.current : 0)
  const sec = (n: number) => Math.floor(n / 1000)
  const ids = new Set([...Object.keys(banked), here])
  return {
    running,
    setRunning,
    seconds: (id: string) => sec(ms(id)),
    total: sec([...ids].reduce((a, id) => a + ms(id), 0)),
  }
}

function useFullScreen(): [boolean, () => void] {
  const [full, setFull] = useState(() => !!document.fullscreenElement)
  useEffect(() => {
    const on = () => setFull(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', on)
    return () => document.removeEventListener('fullscreenchange', on)
  }, [])
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen?.()
    else document.documentElement.requestFullscreen?.().catch(() => {})
  }
  return [full, toggle]
}

export function PlanningPresent() {
  const styles = useStyles()
  const [params] = useSearchParams()
  const cycles = usePlanningCycles()
  const chosen = params.get('cycle') || cycles.data?.[0]?.cycle
  const current = usePlanningCycle(chosen)
  useDocumentTitle(current.data?.plan.title?.replace(/\s*·\s*/g, ' ') || 'Planning')

  let body
  if (cycles.isLoading || current.isLoading) body = <Spinner label="Loading…" />
  else if (current.data) return <Stage data={current.data} lost={!!current.error} />
  else {
    const err = cycles.error ?? current.error
    body = (
      <ErrorCard
        title={chosen ? `Couldn't load ${chosen}` : 'No plan yet'}
        message={apiErrorMessage(err, 'Open the planning page to start one.')}
        retry={() => void (chosen ? current.refetch() : cycles.refetch())}
      />
    )
  }
  return (
    <FluentProvider theme={PRESENT_TYPE}>
      <div className={styles.center}>{body}</div>
    </FluentProvider>
  )
}

function Stage({ data, lost }: { data: PlanningCycle; lost: boolean }) {
  const styles = useStyles()
  const agenda = useAgendaStyles()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const sections = usePlanSections(data)
  const at = Math.max(
    0,
    sections.findIndex((s) => s.id === params.get('s')),
  )
  // Never empty: the goal, scope, decisions and wrap-up are always there.
  const section = sections[at]!
  const [rail, setRail] = useState(true)
  const [full, toggleFull] = useFullScreen()
  const clock = useAgendaClock(section.id)
  const [pending, setPending] = useState<DecisionItem | null>(null)
  const stage = useRef<HTMLElement>(null)
  const open = decisionLog(data.plan, data.state).open.length

  const show = (i: number) => {
    const s = sections[Math.min(sections.length - 1, Math.max(0, i))]!
    setParams({ cycle: data.cycle, s: s.id }, { replace: true })
  }
  const go = (d: DecisionItem) => {
    show(sections.findIndex((s) => s.id === d.section))
    setPending(d)
  }
  // A new section starts at its top, and the keyboard goes along to its
  // heading: the button that was pressed may be gone (the last Next).
  const first = useRef(true)
  useEffect(() => {
    stage.current?.scrollTo?.({ top: 0 })
    if (first.current) first.current = false
    else document.getElementById(`h-plan-${section.id}`)?.focus({ preventScroll: true })
  }, [section.id])
  // Once the item's section is on the stage, bring the item into view.
  useEffect(() => {
    if (!pending || pending.section !== section.id) return
    // Cleared only once it ran: clearing it here would re-run this effect,
    // whose cleanup cancels the frame before it comes.
    const t = requestAnimationFrame(() => {
      spotlight(pending.target, pending.section)
      setPending(null)
    })
    return () => cancelAnimationFrame(t)
  }, [pending, section.id])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
      const el = e.target as HTMLElement | null
      if (
        el?.closest?.('input, textarea, select, [contenteditable], [role="dialog"], [role="menu"]')
      )
        return
      if (e.key === 'ArrowRight' || e.key === 'PageDown') show(at + 1)
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') show(at - 1)
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const exit = () => {
    if (document.fullscreenElement) void document.exitFullscreen?.()
    navigate(`/admin/planning?cycle=${encodeURIComponent(data.cycle)}`)
  }

  const spent = clock.seconds(section.id)
  const planned = (section.minutes ?? 0) * 60
  const late = planned > 0 && spent > planned
  const total = clock.total
  const plannedTotal = sections.reduce((a, s) => a + (s.minutes ?? 0), 0)
  const prev = sections[at - 1]
  const next = sections[at + 1]

  return (
    <FluentProvider theme={PRESENT_TYPE}>
      <div className={styles.root} data-testid="present-root">
        <header className={styles.bar}>
          <h1 className={styles.title}>{data.plan.title || data.cycle}</h1>
          <nav aria-label="Agenda" className={styles.nav}>
            <ol className={mergeClasses(agenda.agenda, styles.agenda)}>
              {sections.map((s, i) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={mergeClasses(agenda.link, i === at && agenda.here)}
                    aria-current={i === at ? 'step' : undefined}
                    onClick={() => show(i)}
                  >
                    <span className={agenda.num}>{i + 1}</span>
                    {s.nav}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          <div className={styles.tools}>
            <ToggleButton
              icon={<PanelRight20Regular />}
              checked={rail}
              onClick={() => setRail(!rail)}
            >
              Decisions
              <Badge
                className={styles.count}
                appearance="tint"
                shape="rounded"
                color={open ? 'warning' : 'success'}
              >
                {open ? `${open} open` : 'No open decisions'}
              </Badge>
            </ToggleButton>
            <Tooltip content={full ? 'Leave full screen' : 'Full screen'} relationship="label">
              <ToggleButton
                icon={full ? <FullScreenMinimize20Regular /> : <FullScreenMaximize20Regular />}
                checked={full}
                onClick={toggleFull}
              />
            </Tooltip>
            <InfoHint text="← → or a clicker's page keys step through the agenda." />
            <Button icon={<ArrowExit20Regular />} onClick={exit}>
              Exit
            </Button>
          </div>
        </header>

        <div className={styles.main}>
          <main className={styles.stage} ref={stage}>
            <div className={styles.inner}>
              <PlanningStatus data={data} />
              {/* Without Issues access GitHub hides items; the room must know the board is short. */}
              {(data.plan.hidden ?? 0) > 0 && (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    {data.plan.hidden} board items are left out: the GitHub token can't read the
                    repositories they're in.
                  </MessageBarBody>
                </MessageBar>
              )}
              {lost && (
                <MessageBar intent="warning">
                  <MessageBarBody>Connection lost. Retrying…</MessageBarBody>
                </MessageBar>
              )}
              <PlanSection
                key={section.id}
                id={`plan-${section.id}`}
                icon={section.icon}
                step={at + 1}
                title={section.title}
                minutes={section.minutes}
                hint={section.hint}
                draft={section.draft}
                extra={section.extra}
              >
                {section.body}
              </PlanSection>
            </div>
          </main>
          {rail && (
            <aside className={styles.rail} aria-label="Decisions">
              <DecisionRail plan={data.plan} state={data.state} onGo={go} />
            </aside>
          )}
        </div>

        <footer className={styles.foot}>
          <div>
            {prev && (
              <Button
                appearance="subtle"
                icon={<ChevronLeft20Regular />}
                onClick={() => show(at - 1)}
              >
                {prev.nav}
              </Button>
            )}
          </div>
          <div className={styles.clock}>
            <Tooltip
              content={clock.running ? 'Pause the clock' : 'Start the clock'}
              relationship="label"
            >
              <ToggleButton
                size="small"
                shape="circular"
                icon={clock.running ? <Pause16Regular /> : <Play16Regular />}
                checked={clock.running}
                onClick={() => clock.setRunning(!clock.running)}
              />
            </Tooltip>
            <div className={styles.time} data-testid="present-clock">
              <span className={mergeClasses(styles.digits, late && styles.over)}>
                {section.nav} {mmss(spent)}
                {planned > 0 && ` of ${section.minutes} min`}
              </span>
              {planned > 0 && (
                <ProgressBar
                  value={Math.min(1, spent / planned)}
                  color={late ? 'warning' : 'brand'}
                  aria-label={`Time on ${section.nav}`}
                />
              )}
            </div>
            <Caption1 className={styles.muted}>
              Total {mmss(total)}
              {plannedTotal > 0 && ` of ${plannedTotal} min`}
            </Caption1>
          </div>
          <div className={styles.next}>
            {next && (
              <Button
                appearance="subtle"
                icon={<ChevronRight20Regular />}
                iconPosition="after"
                onClick={() => show(at + 1)}
              >
                {next.nav}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </FluentProvider>
  )
}

export default PlanningPresent
